// src/admin/components/sales/SaleList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    RiAddLine, RiSearchLine, RiRefreshLine, RiEyeLine,
    RiDeleteBin6Line, RiHandCoinLine, RiBuildingLine,
    RiMoneyDollarCircleLine, RiFilterOffLine, RiCalendarLine, RiPrinterLine
} from 'react-icons/ri';
import { getAllSales, getSale, deleteSale, formatCurrency } from '../../../models/saleModel';
import PrintReceipt from '../payments/PrintReceipt';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
    paid:    { key: 'sale_paid',    bg: 'bg-green-100 text-green-800',  bgDark: 'bg-green-900/20 text-green-400' },
    partial: { key: 'sale_partial', bg: 'bg-yellow-100 text-yellow-800', bgDark: 'bg-yellow-900/20 text-yellow-400' },
    unpaid:  { key: 'sale_unpaid', bg: 'bg-red-100 text-red-800',   bgDark: 'bg-red-900/20 text-red-400' },
};

const SaleList = () => {
    const { t, i18n } = useTranslation();
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const isManagement = isAdmin || user?.role === 'front_desk' || user?.role === 'sales';
    const isRTL = ['ar', 'ku'].includes(i18n.language);

    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [printSale, setPrintSale] = useState(null);
    const [printLoadingId, setPrintLoadingId] = useState(null);

    const fetchSales = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await getAllSales();
            setSales(res.sales || []);
        } catch (err) {
            setError(err.message || t('failed_to_load_sales'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { fetchSales(); }, [fetchSales]);

    const handlePrint = async (id) => {
        try {
            setPrintLoadingId(id);
            const res = await getSale(id);
            setPrintSale({ ...res.sale, paymentType: 'sale' });
        } catch (err) {
            toast.error(err.message || 'Failed to load sale');
        } finally {
            setPrintLoadingId(null);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('delete_sale_confirm'))) return;
        try {
            setDeletingId(id);
            await deleteSale(id);
            toast.success(t('sale_deleted'));
            fetchSales();
        } catch (err) {
            toast.error(err.message || t('failed_to_delete_sale'));
        } finally {
            setDeletingId(null);
        }
    };

    const filtered = sales.filter(s => {
        const q = search.toLowerCase();
        const matchSearch = !q || s.buyerName?.toLowerCase().includes(q) ||
            s.buyerPhone?.toLowerCase().includes(q) ||
            s.apartmentNumber?.toLowerCase().includes(q) ||
            s.buildingName?.toLowerCase().includes(q);
        const matchStatus = !statusFilter || s.paymentStatus === statusFilter;
        return matchSearch && matchStatus;
    });

    const totalRevenue = sales.reduce((sum, s) => sum + (s.totalPrice || 0), 0);
    const totalCollected = sales.reduce((sum, s) => sum + (s.paidAmount || 0), 0);
    const totalPending = totalRevenue - totalCollected;
    const totalCommission = sales.reduce((sum, s) => sum + (s.commissionAmount || 0), 0);

    const cardBase = `rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`;
    const thClass = `px-5 py-3 text-xs font-semibold uppercase tracking-wider text-start ${isDarkMode ? 'text-zinc-400 bg-zinc-950' : 'text-zinc-500 bg-zinc-50'}`;
    const tdClass = `px-5 py-4 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`;

    return (
        <div className="p-4 md:p-6" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                        {t('sales')}
                    </h1>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                        {t('sales_subtitle')}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={fetchSales}
                        className={`p-2 rounded-lg border transition-colors ${isDarkMode ? 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:bg-zinc-800' : 'border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50'}`}
                    >
                        <RiRefreshLine size={18} />
                    </button>
                    {isManagement && (
                        <Link
                            to="/admin/sales/add"
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors text-sm"
                        >
                            <RiAddLine size={18} />
                            {t('new_sale')}
                        </Link>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className={`grid grid-cols-1 sm:grid-cols-3 ${totalCommission > 0 ? 'lg:grid-cols-4' : ''} gap-4 mb-6`}>
                {[
                    { label: t('total_sales'), value: sales.length, icon: <RiHandCoinLine size={20} />, color: 'text-blue-500' },
                    { label: t('total_revenue'), value: formatCurrency(totalRevenue), icon: <RiMoneyDollarCircleLine size={20} />, color: 'text-green-500' },
                    { label: t('outstanding_balance'), value: formatCurrency(totalPending), icon: <RiMoneyDollarCircleLine size={20} />, color: 'text-red-500' },
                    ...(totalCommission > 0 ? [{
                        label: t('total_commission_paid') || 'Commission Paid',
                        value: `− ${formatCurrency(totalCommission)}`,
                        icon: <RiHandCoinLine size={20} />,
                        color: 'text-red-500'
                    }] : []),
                ].map((stat, i) => (
                    <div key={i} className={cardBase}>
                        <div className="flex items-center justify-between mb-1">
                            <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{stat.label}</span>
                            <span className={stat.color}>{stat.icon}</span>
                        </div>
                        <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{stat.value}</p>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className={`${cardBase} mb-6 flex flex-col sm:flex-row gap-3`}>
                <div className="relative flex-1">
                    <RiSearchLine className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-zinc-400`} size={16} />
                    <input
                        type="text"
                        placeholder={t('search_sales_placeholder')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className={`w-full ${isRTL ? 'pr-9 pl-4' : 'pl-9 pr-4'} py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-red-500
                            ${isDarkMode ? 'bg-zinc-950 border-zinc-700 text-white placeholder:text-zinc-600' : 'bg-zinc-50 border-zinc-200 text-zinc-900 placeholder:text-zinc-400'}`}
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    className={`px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-red-500
                        ${isDarkMode ? 'bg-zinc-950 border-zinc-700 text-white' : 'bg-zinc-50 border-zinc-200 text-zinc-900'}`}
                >
                    <option value="">{t('all_statuses')}</option>
                    <option value="unpaid">{t('sale_unpaid')}</option>
                    <option value="partial">{t('sale_partial')}</option>
                    <option value="paid">{t('sale_paid')}</option>
                </select>
                {(search || statusFilter) && (
                    <button
                        onClick={() => { setSearch(''); setStatusFilter(''); }}
                        className={`flex items-center gap-1 px-3 py-2 rounded-lg border text-sm transition-colors
                            ${isDarkMode ? 'border-zinc-700 text-zinc-400 hover:bg-zinc-800' : 'border-zinc-200 text-zinc-500 hover:bg-zinc-100'}`}
                    >
                        <RiFilterOffLine size={16} /> {t('clear_filters')}
                    </button>
                )}
            </div>

            {/* Table */}
            <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-white'}`}>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr>
                                <th className={thClass}>{t('buyer')}</th>
                                <th className={thClass}>{t('apartments')}</th>
                                <th className={thClass}>{t('total_sale_price')}</th>
                                <th className={thClass}>{t('collected')}</th>
                                <th className={thClass}>{t('balance')}</th>
                                <th className={thClass}>{t('next_payment')}</th>
                                <th className={thClass}>{t('sale_progress')}</th>
                                <th className={thClass}>{t('status')}</th>
                                <th className={`${thClass} text-center`}>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${isDarkMode ? 'divide-zinc-800' : 'divide-zinc-100'}`}>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        {Array.from({ length: 8 }).map((_, j) => (
                                            <td key={j} className="px-5 py-4">
                                                <div className={`h-4 rounded ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-100'}`} />
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : error ? (
                                <tr>
                                    <td colSpan={8} className="px-5 py-10 text-center text-red-400">{error}</td>
                                </tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-5 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center gap-3">
                                            <div className={`p-4 rounded-full ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-100'}`}>
                                                <RiHandCoinLine size={36} className={isDarkMode ? 'text-zinc-600' : 'text-zinc-400'} />
                                            </div>
                                            <p className={`font-medium ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>{t('no_sales_found')}</p>
                                            {isManagement && (
                                                <Link to="/admin/sales/add" className="text-sm text-red-500 hover:underline flex items-center gap-1">
                                                    <RiAddLine size={14} /> {t('record_first_sale')}
                                                </Link>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ) : filtered.map(sale => {
                                const pct = sale.totalPrice > 0 ? Math.min(100, Math.round((sale.paidAmount / sale.totalPrice) * 100)) : 0;
                                const status = STATUS_CONFIG[sale.paymentStatus] || STATUS_CONFIG.unpaid;
                                return (
                                    <tr key={sale.id} className={`transition-colors ${isDarkMode ? 'hover:bg-zinc-800/50' : 'hover:bg-zinc-50/50'}`}>
                                        <td className={tdClass}>
                                            <div className="flex items-center gap-3">
                                                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-white text-xs font-bold">{sale.buyerName?.charAt(0)}</span>
                                                </div>
                                                <div>
                                                    <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{sale.buyerName}</p>
                                                    <p className="text-xs text-zinc-500" dir="ltr" style={{ textAlign: isRTL ? 'right' : 'left' }}>{sale.buyerPhone}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className={tdClass}>
                                            {sale.apartmentNumber ? (
                                                <div>
                                                    <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{sale.apartmentNumber}</p>
                                                    <p className="text-xs text-zinc-500 flex items-center gap-1">
                                                        <RiBuildingLine size={11} /> {sale.buildingName}
                                                    </p>
                                                </div>
                                            ) : <span className="text-zinc-400 text-xs">—</span>}
                                        </td>
                                        <td className={`${tdClass} font-semibold`}>
                                            <span className={isDarkMode ? 'text-white' : 'text-zinc-900'}>{formatCurrency(sale.totalPrice)}</span>
                                        </td>
                                        <td className={tdClass}>
                                            <span className="text-green-500 font-medium">{formatCurrency(sale.paidAmount)}</span>
                                        </td>
                                        <td className={tdClass}>
                                            <span className={sale.balance > 0 ? 'text-red-400 font-medium' : 'text-green-400 font-medium'}>
                                                {formatCurrency(sale.balance)}
                                            </span>
                                        </td>
                                        <td className={tdClass}>
                                            {sale.nextPaymentDate ? (
                                                <div className="flex flex-col">
                                                    <span className={`text-sm font-semibold ${isDarkMode ? 'text-zinc-100' : 'text-zinc-900'}`}>
                                                        {formatCurrency(sale.nextPaymentAmount)}
                                                    </span>
                                                    <span className="text-xs text-zinc-500 flex items-center gap-1">
                                                        <RiCalendarLine size={12} />
                                                        {new Date(sale.nextPaymentDate).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : i18n.language)}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-zinc-400 text-xs">—</span>
                                            )}
                                        </td>
                                        <td className={`${tdClass} min-w-[100px]`}>
                                            <div className="flex items-center gap-2">
                                                <div className={`flex-1 h-1.5 rounded-full ${isDarkMode ? 'bg-zinc-700' : 'bg-zinc-200'}`}>
                                                    <div
                                                        className={`h-1.5 rounded-full ${pct >= 100 ? 'bg-green-500' : pct > 0 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                                <span className="text-xs text-zinc-500 w-8 text-end">{pct}%</span>
                                            </div>
                                        </td>
                                        <td className={tdClass}>
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${isDarkMode ? status.bgDark : status.bg}`}>
                                                {t(status.key)}
                                            </span>
                                        </td>
                                        <td className={`${tdClass} text-center`}>
                                            <div className="flex items-center justify-center gap-2">
                                                <Link
                                                    to={`/admin/sales/${sale.id}`}
                                                    className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-zinc-400 hover:bg-zinc-700 hover:text-white' : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900'}`}
                                                    title={t('view_details')}
                                                >
                                                    <RiEyeLine size={17} />
                                                </Link>
                                                <button
                                                    onClick={() => handlePrint(sale.id)}
                                                    disabled={printLoadingId === sale.id}
                                                    className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-zinc-400 hover:bg-zinc-700 hover:text-white' : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900'}`}
                                                    title={t('print')}
                                                >
                                                    {printLoadingId === sale.id
                                                        ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                        : <RiPrinterLine size={17} />}
                                                </button>
                                                {isAdmin && (
                                                    <button
                                                        onClick={() => handleDelete(sale.id)}
                                                        disabled={deletingId === sale.id}
                                                        className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-zinc-400 hover:bg-red-900/30 hover:text-red-400' : 'text-zinc-500 hover:bg-red-50 hover:text-red-600'}`}
                                                        title={t('delete')}
                                                    >
                                                        <RiDeleteBin6Line size={17} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {printSale && (
                <PrintReceipt
                    payment={printSale}
                    type="sale"
                    onClose={() => setPrintSale(null)}
                />
            )}
        </div>
    );
};

export default SaleList;
